import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search, Eye, RefreshCw, FileText } from "lucide-react";
import { useState } from "react";
import { Button, Input, Badge, Card, Spinner } from "@/components/ui/primitives";
import { api, type ApplicationSummary, type TabCounts } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/")({ component: ApplicationsPage });

const TABS = [
  { key: "all", label: "All" },
  { key: "unprocessed", label: "Unprocessed" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
] as const;

function ApplicationsPage() {
  const [tab, setTab] = useState<string>("all");
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["applications", tab, q],
    queryFn: () => api.listApplications(tab, q),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Applications</h1>
          <p className="mt-1 text-sm text-muted-foreground">Active credit-approval cases</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search applicant…" className="w-64 pl-8" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>
      </div>

      <TabBar tab={tab} setTab={setTab} counts={data?.counts} />

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-5 py-3 font-medium">Applicant</th>
              <th className="px-5 py-3 font-medium">Age</th>
              <th className="px-5 py-3 font-medium">Product</th>
              <th className="px-5 py-3 font-medium">Amount</th>
              <th className="px-5 py-3 font-medium">Stage</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Tier</th>
              <th className="px-5 py-3 font-medium">Submitted</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-5 py-12 text-center text-muted-foreground">
                  <Spinner className="inline-block" /> Loading…
                </td>
              </tr>
            )}
            {!isLoading && (data?.items.length ?? 0) === 0 && (
              <tr>
                <td colSpan={9} className="px-5 py-12 text-center text-muted-foreground">
                  No applications in this tab.
                </td>
              </tr>
            )}
            {data?.items.map((a, i) => <Row key={a.id} app={a} first={i === 0} />)}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function TabBar({ tab, setTab, counts }: { tab: string; setTab: (t: string) => void; counts?: TabCounts }) {
  return (
    <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1 w-fit">
      {TABS.map((t) => {
        const n = counts
          ? t.key === "all"
            ? counts.total
            : t.key === "unprocessed"
              ? counts.unprocessed
              : t.key === "in_progress"
                ? counts.in_progress
                : counts.completed
          : undefined;
        return (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "flex items-center gap-2 rounded-sm px-3 py-1.5 text-sm transition-colors",
              tab === t.key ? "bg-background font-medium shadow-sm" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
            {n !== undefined && (
              <span
                className={cn(
                  "flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-medium",
                  tab === t.key ? "bg-muted text-foreground" : "bg-muted/60 text-muted-foreground",
                )}
              >
                {n}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Row({ app, first }: { app: ApplicationSummary; first: boolean }) {
  const navigate = useNavigate();
  const target = `/apps/${app.id}/${app.stage === "decision" ? "decision" : app.stage}`;
  return (
    <tr className={cn("group hover:bg-muted/30 transition-colors cursor-pointer", !first && "border-t border-border")} onClick={() => navigate({ to: target })}>
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
          <span className="font-medium">{app.customer_name}</span>
        </div>
      </td>
      <td className="px-5 py-3.5 text-muted-foreground">{app.customer_age}</td>
      <td className="px-5 py-3.5 text-muted-foreground">{app.product_label}</td>
      <td className="px-5 py-3.5 font-medium tabular-nums">{app.amount_display}</td>
      <td className="px-5 py-3.5"><StageBadge stage={app.stage} /></td>
      <td className="px-5 py-3.5"><StatusChip status={app.status} label={app.status_label} /></td>
      <td className="px-5 py-3.5">
        {app.risk_tier ? <Badge variant="muted">{app.risk_tier}</Badge> : <span className="text-muted-foreground">—</span>}
      </td>
      <td className="px-5 py-3.5 text-muted-foreground">{app.submitted_ago}</td>
      <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {!app.is_finalized && (
            <Button variant="ghost" size="icon" className="h-8 w-8" title="Re-run stage">
              <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.9} />
            </Button>
          )}
          <Link to={target}>
            <Button variant="ghost" size="icon" className="h-8 w-8" title="View">
              <Eye className="h-3.5 w-3.5" strokeWidth={1.9} />
            </Button>
          </Link>
        </div>
      </td>
    </tr>
  );
}

function StageBadge({ stage }: { stage: string }) {
  const labels: Record<string, string> = { intake: "Intake", analysis: "Analysis", coordination: "Coordination", decision: "Decision" };
  return <Badge variant="outline" className="capitalize">{labels[stage] ?? stage}</Badge>;
}

function StatusChip({ status, label }: { status: string; label: string }) {
  const tone: "success" | "warn" | "destructive" | "info" | "muted" = (() => {
    switch (status) {
      case "auto_approved":
      case "approved_with_discretion":
        return "success";
      case "pending_review":
      case "needs_info":
        return "warn";
      case "fraud_hold":
      case "auto_declined":
      case "declined":
        return "destructive";
      case "in_flight":
        return "info";
      default:
        return "muted";
    }
  })();
  return <Badge variant={tone}>{label}</Badge>;
}
